import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2, Mail, ScrollText, LogOut, ShieldCheck, Menu, X, ExternalLink, Inbox,
  Users, SlidersHorizontal, Target, FileText,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/components/ui';

const NAV = [
  { to: '/admin', label: 'Organisations', icon: Building2, end: true },
  // Second, because it is the only tab that represents work rather than a
  // question: feedback nobody has read, and customers waiting on us.
  { to: '/admin/inbox', label: 'Inbox', icon: Inbox },
  // Leads before Email, because the two are read in that order: find who to
  // write to, then write to them. Email stays where it is — it is for people
  // who are already customers, which is a different job on a different list.
  { to: '/admin/leads', label: 'Leads', icon: Target },
  { to: '/admin/email', label: 'Email', icon: Mail },
  { to: '/admin/templates', label: 'Templates', icon: FileText },
  { to: '/admin/activity', label: 'Activity', icon: ScrollText },
  { to: '/admin/operators', label: 'Operators', icon: Users },
  { to: '/admin/settings', label: 'Settings', icon: SlidersHorizontal },
];

/**
 * The console's chrome.
 *
 * Visually distinct from the manager app on purpose — a different accent, the
 * word "console" in the corner, no organisation name anywhere. Somebody with
 * both open in two tabs should never have to check which is which before
 * clicking, because the two do very different things to the same data.
 */
export default function PlatformShell() {
  const { admin, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  function leave() {
    signOut();
    navigate('/admin/login');
  }

  return (
    <div className="min-h-screen bg-bg text-ink flex flex-col">
      <header className="border-b border-line bg-raised/40 sticky top-0 z-20 backdrop-blur">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-6">
          <div className="h-16 flex items-center justify-between gap-4">
            {/*
              Never the thing that gives way.

              The name in the corner is how somebody with the console and the
              manager app open in two tabs tells them apart, so it is the last
              thing that should be truncated — and it was the first, because it
              sat in a flex row against a nav that kept growing.
            */}
            <div className="flex items-center gap-2.5 shrink-0">
              <ShieldCheck className="h-5 w-5 text-brand shrink-0" />
              <span className="font-extrabold tracking-tight">PROVIA console</span>
            </div>

            {/*
              Eight tabs is as many as this bar can hold, and it holds them by
              giving up the icon before it gives up the word.

              Between lg and xl the icons are hidden and only the labels
              remain: a label with no icon is still a tab anybody can use,
              while an icon with no label is a guessing game. Below lg not even
              the words fit, and the menu button is the honest answer rather
              than a row that overlaps the sign-out button — which is exactly
              what the sixth, seventh and eighth tabs did to it.
            */}
            <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1 min-w-0">
              {NAV.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) => cn(
                    'flex items-center gap-2 px-2.5 xl:px-3 h-9 rounded-xl text-sm transition-colors',
                    isActive ? 'bg-brand/15 text-ink font-medium' : 'text-muted hover:text-ink',
                  )}
                >
                  <Icon className="hidden xl:block h-4 w-4" />
                  {label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2 shrink-0">
              {/*
                The signed-in address is on the button rather than beside it.
                An operator does not need to be told their own email on every
                screen, and it was taking a fifth of the header to say it —
                which is what pushed the title into an ellipsis. It is still
                one hover away, and in the menu below.
              */}
              <button
                onClick={leave}
                title={admin?.email ? `Signed in as ${admin.email}` : 'Sign out'}
                aria-label="Sign out"
                className="hidden lg:flex items-center gap-2 px-2.5 xl:px-3 h-9 rounded-xl text-sm
                           text-muted hover:text-ink transition-colors"
              >
                <LogOut className="h-4 w-4" />
                {/* The word goes before the tabs do. */}
                <span className="hidden xl:inline">Sign out</span>
              </button>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="lg:hidden p-2 -mr-2 text-muted hover:text-ink"
                aria-label="Menu"
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {menuOpen && (
            <nav className="lg:hidden pb-3 space-y-1">
              {NAV.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) => cn(
                    'flex items-center gap-2 px-3 h-10 rounded-xl text-sm',
                    isActive ? 'bg-brand/15 text-ink font-medium' : 'text-muted',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}
              <button
                onClick={leave}
                className="w-full flex items-center gap-2 px-3 h-10 rounded-xl text-sm text-muted"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </button>
              {/* Which account, for a shared laptop. Once, and out of the way. */}
              {admin?.email && (
                <p className="px-3 pt-1 text-xs text-faint truncate">
                  Signed in as {admin.email}
                </p>
              )}
            </nav>
          )}
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-7xl px-5 sm:px-6 py-6 sm:py-8">
        <Outlet />
      </main>

      <footer className="border-t border-line">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-6 py-4 flex items-center
                        justify-between gap-4 text-sm text-faint">
          <span>
            Every change made here is recorded against your name in the audit log.
          </span>
          <a href="/login" className="flex items-center gap-1.5 hover:text-ink shrink-0">
            Manager app <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </footer>
    </div>
  );
}
